import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import type {
  LnpCreateRequest,
  LnpLineEntrata,
  LnpLineUscita,
} from '@produzionejb/types';
import { ApiError } from '../../lib/api-client';
import {
  useCreateLnp,
  useLnp,
  useUpdateLnp,
  useCloseLnp,
  useDeleteLnp,
} from '../../lib/lnp-api';
import { LotPicker } from '../../components/LotPicker';
import { ProductPicker } from '../../components/ProductPicker';
import { BackToStockMovementsLink } from '../../components/BackToStockMovementsLink';

type EntrataRow = {
  key: string;
  lotId: string;
  lotCode?: string;
  // Drive PSC: stesso lotto puo entrare con un Product diverso da Lot.productId
  // (cernita/upgrade: es. lotto HQ CBD BOOST SMALL BUDS -> riclassificato SMALL BUDS MIX CBD BOOST).
  // Default null = il close handler usa Lot.productId. Override solo se l'operatore lo cambia.
  productId: string | null;
  productCode?: string | null;
  productName?: string | null;
  qty: string;
  note?: string;
};

type UscitaRow = {
  key: string;
  lotId: string;
  lotCode?: string;
  productId: string | null;
  productCode?: string | null;
  productName?: string | null;
  qty: string;
  pesoIniziale: string;
  pesoFinale: string;
  hasMold: boolean;
  isDry: boolean;
  isWet: boolean;
  note?: string;
};

function newKey() {
  return Math.random().toString(36).slice(2, 9);
}

function todayIso() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function sumNumStrings(rows: { qty: string }[]): number {
  return rows.reduce((acc, r) => acc + (Number(r.qty) || 0), 0);
}

export function LnpFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const lnpQ = useLnp(id);
  const createMut = useCreateLnp();
  const updateMut = useUpdateLnp(id ?? '');
  const closeMut = useCloseLnp(id ?? '');
  const deleteMut = useDeleteLnp();

  const [draftName, setDraftName] = useState('');
  const [responsable, setResponsable] = useState('');
  const [workDate, setWorkDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [entrate, setEntrate] = useState<EntrataRow[]>([]);
  const [uscite, setUscite] = useState<UscitaRow[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const readOnly = isEdit && lnpQ.data?.status !== 'DRAFT';

  useEffect(() => {
    if (isEdit && lnpQ.data) {
      const p = lnpQ.data;
      setDraftName(p.draftName ?? '');
      setResponsable(p.responsable ?? '');
      setWorkDate(p.workDate.slice(0, 10));
      setNotes(p.notes ?? '');
      setEntrate(
        p.entrate.map((l) => ({
          key: l.id,
          lotId: l.lotId ?? '',
          lotCode: l.lotCode ?? undefined,
          productId: l.productId ?? null,
          productCode: l.productCode,
          productName: l.productName,
          qty: l.qty,
          note: l.note ?? '',
        })),
      );
      setUscite(
        p.uscite.map((l) => ({
          key: l.id,
          lotId: l.lotId ?? '',
          lotCode: l.lotCode ?? undefined,
          productId: l.productId ?? null,
          productCode: l.productCode,
          productName: l.productName,
          qty: l.qty,
          pesoIniziale: l.pesoIniziale ?? '',
          pesoFinale: l.pesoFinale ?? '',
          hasMold: l.hasMold,
          isDry: l.isDry,
          isWet: l.isWet,
          note: l.note ?? '',
        })),
      );
    }
  }, [isEdit, lnpQ.data]);

  const entrataTot = useMemo(() => sumNumStrings(entrate), [entrate]);
  const uscitaTot = useMemo(() => sumNumStrings(uscite), [uscite]);
  const sfrido = entrataTot - uscitaTot;
  const sfridoPct = entrataTot > 0 ? (sfrido / entrataTot) * 100 : 0;

  function addEntrata() {
    setEntrate((rows) => [...rows, { key: newKey(), lotId: '', productId: null, qty: '' }]);
  }
  function removeEntrata(key: string) {
    setEntrate((rows) => rows.filter((r) => r.key !== key));
  }
  function updateEntrata(key: string, patch: Partial<EntrataRow>) {
    setEntrate((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addUscita() {
    setUscite((rows) => [
      ...rows,
      {
        key: newKey(),
        lotId: '',
        productId: null,
        qty: '',
        pesoIniziale: '',
        pesoFinale: '',
        hasMold: false,
        isDry: false,
        isWet: false,
      },
    ]);
  }
  function removeUscita(key: string) {
    setUscite((rows) => rows.filter((r) => r.key !== key));
  }
  function updateUscita(key: string, patch: Partial<UscitaRow>) {
    setUscite((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function buildPayload(): LnpCreateRequest {
    return {
      draftName: draftName.trim(),
      responsable: responsable.trim() || null,
      workDate: new Date(workDate),
      notes: notes.trim() || null,
      entrate: entrate
        .filter((r) => r.lotId && r.qty)
        .map<LnpLineEntrata>((r) => ({
          lotId: r.lotId,
          productId: r.productId,
          qty: Number(r.qty),
          note: r.note?.trim() || null,
        })),
      uscite: uscite
        .filter((r) => r.lotId && r.qty)
        .map<LnpLineUscita>((r) => ({
          lotId: r.lotId,
          productId: r.productId,
          qty: Number(r.qty),
          pesoIniziale: r.pesoIniziale ? Number(r.pesoIniziale) : null,
          pesoFinale: r.pesoFinale ? Number(r.pesoFinale) : null,
          hasMold: r.hasMold,
          isDry: r.isDry,
          isWet: r.isWet,
          note: r.note?.trim() || null,
        })),
    };
  }

  async function handleSave() {
    setErrorMsg(null);
    const payload = buildPayload();
    if (!payload.draftName) {
      setErrorMsg(t('lnp.form.errorDraftNameRequired'));
      return;
    }
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(payload);
      } else {
        const created = await createMut.mutateAsync(payload);
        navigate(`/documents/lnp/${created.id}/edit`);
        return;
      }
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleClose() {
    if (!id) return;
    if (entrate.length === 0 || uscite.length === 0) {
      setErrorMsg(t('lnp.form.errorEmptyLines'));
      return;
    }
    if (!window.confirm(t('lnp.form.confirmClose'))) return;
    setErrorMsg(null);
    try {
      // Salva eventuali modifiche pending prima del close
      const payload = buildPayload();
      await updateMut.mutateAsync(payload);
      await closeMut.mutateAsync();
      navigate('/documents/lnp');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm(t('lnp.list.confirmDelete', { name: draftName || `PSC` }))) return;
    try {
      await deleteMut.mutateAsync(id);
      navigate('/documents/lnp');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  const isSubmitting = createMut.isPending || updateMut.isPending || closeMut.isPending;

  if (isEdit && lnpQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && lnpQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('lnp.form.errorLoad')}: {(lnpQ.error as Error).message}
      </p>
    );
  }

  const titleStatus = isEdit
    ? lnpQ.data?.code
      ? `PSC ${lnpQ.data.code}`
      : (lnpQ.data?.draftName ?? t('lnp.form.titleEdit'))
    : t('lnp.form.titleNew');

  return (
    <div className="space-y-4">
      <BackToStockMovementsLink />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{titleStatus}</h2>
        <div className="flex items-center gap-2">
          {isEdit && lnpQ.data && (
            <span
              className={
                lnpQ.data.status === 'DRAFT'
                  ? 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700'
                  : 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
              }
            >
              {lnpQ.data.status === 'DRAFT' ? t('lnp.list.statusDraft') : t('lnp.list.statusClosed')}
            </span>
          )}
          <Button variant="secondary" onClick={() => navigate('/documents/lnp')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <Card>
          <CardContent>
            <p className="text-sm text-red-600">{errorMsg}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('lnp.form.sectionHeader')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="draftName">{t('lnp.fields.draftName')} *</Label>
            <Input
              id="draftName"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={t('lnp.form.draftNamePlaceholder')}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="responsable">{t('lnp.fields.responsable')}</Label>
            <Input
              id="responsable"
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              placeholder={t('lnp.form.responsablePlaceholder')}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="workDate">{t('lnp.fields.workDate')} *</Label>
            <Input
              id="workDate"
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('lnp.fields.entrataTot')} / {t('lnp.fields.uscitaTot')} / {t('lnp.fields.sfridoPct')}</Label>
            <p className="font-mono text-sm">
              {entrataTot.toFixed(2)}g / {uscitaTot.toFixed(2)}g /{' '}
              <span className={sfrido < 0 ? 'text-red-600' : 'text-slate-700'}>
                {sfridoPct.toFixed(2)}%
              </span>
            </p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="notes">{t('lnp.fields.notes')}</Label>
            <textarea
              id="notes"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      {/* NOMENCLATURA — allineata a Drive/Excel:
       *   Card 1 ("Uscite dal magazzino principale") = array `entrate` nel codice
       *     = grezzi prelevati. line.direction='IN' -> SM.OUT al close.
       *   Card 2 ("Entrate nel magazzino produzione") = array `uscite` nel codice
       *     = lavorati rilasciati. line.direction='OUT' -> SM.IN al close.
       * Lo scambio terminologico UI/codice e' intenzionale: i nomi `entrate`/
       * `uscite` interni riflettono il flusso del documento LNP, i label UI
       * riflettono il flusso del magazzino (convenzione Drive).
       */}
      <Card>
        <CardHeader>
          <CardTitle>{t('lnp.form.sectionEntrate')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {entrate.length === 0 && (
            <p className="text-sm text-slate-500">{t('lnp.form.emptyEntrate')}</p>
          )}
          {entrate.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-2 py-2">{t('lnp.fields.lotto')}</th>
                  <th className="px-2 py-2">{t('lnp.fields.prodottoOverride')}</th>
                  <th className="px-2 py-2 text-right">{t('lnp.fields.pesoEntrata')} (g)</th>
                  <th className="px-2 py-2">{t('lnp.fields.note')}</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {entrate.map((row) => (
                  <tr key={row.key} className="border-b border-slate-100">
                    <td className="px-2 py-1">
                      <LotPicker
                        value={row.lotId}
                        valueCode={row.lotCode}
                        onChange={(id2, code) =>
                          updateEntrata(row.key, {
                            lotId: id2,
                            lotCode: code,
                            // Cambio lotto -> reset override product (l'operatore puo' settarlo dopo se vuole forzare un product diverso)
                            productId: null,
                            productCode: null,
                            productName: null,
                          })
                        }
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <ProductPicker
                        value={row.productId ?? ''}
                        valueCode={row.productCode}
                        valueName={row.productName}
                        onChange={(id2, code, name) =>
                          updateEntrata(row.key, { productId: id2, productCode: code, productName: name })
                        }
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        type="number"
                        step="0.0001"
                        className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                        value={row.qty}
                        onChange={(e) => updateEntrata(row.key, { qty: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        value={row.note ?? ''}
                        onChange={(e) => updateEntrata(row.key, { note: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => removeEntrata(row.key)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addEntrata}>
              + {t('lnp.form.addEntrata')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('lnp.form.sectionUscite')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {uscite.length === 0 && (
            <p className="text-sm text-slate-500">{t('lnp.form.emptyUscite')}</p>
          )}
          {uscite.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-2">{t('lnp.fields.lotto')}</th>
                    <th className="px-2 py-2">{t('lnp.fields.prodottoOverride')}</th>
                    <th className="px-2 py-2 text-right">{t('lnp.fields.pesoIniziale')}</th>
                    <th className="px-2 py-2 text-right">{t('lnp.fields.pesoFinale')}</th>
                    <th className="px-2 py-2 text-right">{t('lnp.fields.pesoUscita')}</th>
                    <th className="px-2 py-2">{t('lnp.fields.muffa')}</th>
                    <th className="px-2 py-2">{t('lnp.fields.secca')}</th>
                    <th className="px-2 py-2">{t('lnp.fields.bagnata')}</th>
                    <th className="px-2 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {uscite.map((row) => (
                    <tr key={row.key} className="border-b border-slate-100">
                      <td className="px-2 py-1">
                        <LotPicker
                          value={row.lotId}
                          valueCode={row.lotCode}
                          onChange={(id2, code) =>
                            updateUscita(row.key, {
                              lotId: id2,
                              lotCode: code,
                              productId: null,
                              productCode: null,
                              productName: null,
                            })
                          }
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <ProductPicker
                          value={row.productId ?? ''}
                          valueCode={row.productCode}
                          valueName={row.productName}
                          onChange={(id2, code, name) =>
                            updateUscita(row.key, { productId: id2, productCode: code, productName: name })
                          }
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input
                          type="number"
                          step="0.0001"
                          className="w-20 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                          value={row.pesoIniziale}
                          onChange={(e) => updateUscita(row.key, { pesoIniziale: e.target.value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input
                          type="number"
                          step="0.0001"
                          className="w-20 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                          value={row.pesoFinale}
                          onChange={(e) => updateUscita(row.key, { pesoFinale: e.target.value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input
                          type="number"
                          step="0.0001"
                          className="w-20 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                          value={row.qty}
                          onChange={(e) => updateUscita(row.key, { qty: e.target.value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="checkbox"
                          checked={row.hasMold}
                          onChange={(e) => updateUscita(row.key, { hasMold: e.target.checked })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="checkbox"
                          checked={row.isDry}
                          onChange={(e) => updateUscita(row.key, { isDry: e.target.checked })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="checkbox"
                          checked={row.isWet}
                          onChange={(e) => updateUscita(row.key, { isWet: e.target.checked })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => removeUscita(row.key)}
                            className="text-red-600 hover:underline text-xs"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addUscita}>
              + {t('lnp.form.addUscita')}
            </Button>
          )}
        </CardContent>
      </Card>

      {!readOnly && (
        <div className="flex justify-between gap-2">
          <div>
            {isEdit && (
              <Button
                variant="secondary"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="!bg-red-50 !text-red-700 !border-red-200 hover:!bg-red-100"
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleSave} disabled={isSubmitting}>
              {t('lnp.form.saveButton')}
            </Button>
            {isEdit && (
              <Button onClick={handleClose} disabled={isSubmitting}>
                {t('lnp.form.closeButton')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
